import React from 'react';
import { useTranslation } from 'react-i18next';

import { CopyIcon, InfoIcon } from '../icons';
import { formatTtl, type DnsRecordView } from '../../lib/dns';
import {
  Wrap,
  HeaderRow,
  HeaderCol,
  HeaderCell,
  InfoGlyph,
  Body,
  RowGrid,
  Cell,
  TypeText,
  CellText,
  CopyBtn,
} from './DnsTable.styles';

export interface DnsTableProps {
  records: DnsRecordView[];
  /** Copy a field value to the clipboard (page-level toast handled by caller). */
  onCopy: (value: string) => void;
}

/**
 * Desktop DNS-records table (Type · Name · Data · TTL). The owner copies each
 * Name/Data value into their registrar to point the domain at the platform.
 */
export const DnsTable = ({ records, onCopy }: DnsTableProps): React.ReactElement => {
  const { t } = useTranslation();

  return (
    <Wrap>
      <HeaderRow>
        <HeaderCol>
          <HeaderCell text="Type" tag="span" $strong />
          <InfoGlyph>
            <InfoIcon size={12} />
          </InfoGlyph>
        </HeaderCol>
        <HeaderCol>
          <HeaderCell text="Name" tag="span" />
        </HeaderCol>
        <HeaderCol>
          <HeaderCell text="Data" tag="span" />
        </HeaderCol>
        <HeaderCol>
          <HeaderCell text="TTL" tag="span" />
        </HeaderCol>
      </HeaderRow>
      <Body>
        {records.map((r, i) => (
          <RowGrid key={`${r.type}-${r.host}-${i}`}>
            <Cell>
              <TypeText text={r.type} tag="span" />
            </Cell>
            <Cell>
              <CellText text={r.host} tag="span" title={r.host} />
              <CopyBtn type="button" aria-label={t('Copy name')} onClick={() => onCopy(r.host)}>
                <CopyIcon size={15} />
              </CopyBtn>
            </Cell>
            <Cell>
              <CellText text={r.data} tag="span" title={r.data} />
              <CopyBtn type="button" aria-label={t('Copy data')} onClick={() => onCopy(r.data)}>
                <CopyIcon size={15} />
              </CopyBtn>
            </Cell>
            <Cell>
              <CellText text={formatTtl(r.ttl)} tag="span" />
            </Cell>
          </RowGrid>
        ))}
      </Body>
    </Wrap>
  );
};

export default DnsTable;